import { type FC } from 'react';
import { useIntl } from 'react-intl';

import { useLoginState, useExperiment } from '@xing-com/hub';
import {
  IconCompanies,
  IconCompaniesFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconWeOffer,
  IconJobsFilled,
  IconNews,
  IconNewsFilled,
} from '@xing-com/icons';
import { Section } from '@xing-com/layout-primitives';
import { ProductsMenuWithItemsLoggedOut } from '@xing-com/platform-layout-products-menu';
import { SkipNavigation } from '@xing-com/platform-layout-skip-navigation';

import { ActivityCenterButton } from './activity-center-button/activity-center-button';
import { HeaderNavItemsLoggedIn } from './header-nav-items-logged-in';
import {
  StyledOuterActionsContainer,
  StyledHeader,
  StyledHeaderPlacebo,
  StyledHeaderWrapper,
  StyledLogo,
  StyledLogoWrapper,
  StyledButterfly,
  StyledOnlyOnBigScreenContainer,
  ActionButtonSpace,
} from './header.styles';
import { LoginButton } from './login-button/login-button';
import { NavItemHeader } from './nav-item-header';

const HeaderLogo: FC = () => {
  return (
    <StyledLogoWrapper>
      <StyledLogo dataTestId="logo" />
      <StyledButterfly dataTestId="logoMobile" />
    </StyledLogoWrapper>
  );
};

export type HeaderProps = {
  className?: string;
  loginLink?: string;
  loginTracking?: { sc_o: string };
};

export const Header: React.FC<HeaderProps> = ({ className, loginLink }) => {
  const { formatMessage } = useIntl();
  const isLoggedOut = useLoginState() === 'LOGGED_OUT';
  const focusViewLOEnabled = useExperiment('ABACUS-507') === 'B';

  return (
    <StyledHeaderPlacebo $isLoggedOut={isLoggedOut} className={className}>
      <StyledHeaderWrapper>
        <SkipNavigation />
        <Section noBlockPadding>
          <StyledHeader>
            <HeaderLogo />
            <StyledOuterActionsContainer
              aria-label="header-navigation"
              data-testid="header-navigation"
              as="nav"
            >
              {isLoggedOut ? (
                <>
                  <StyledOnlyOnBigScreenContainer>
                    <NavItemHeader
                      activeItemValues={['jobs']}
                      icon={IconJobs}
                      activeIcon={IconJobsFilled}
                      title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
                      href="https://www.xing.com/jobs"
                      tracking={{
                        nwt_nav: 'find_jobs',
                        sc_o: 'navigation_find_jobs',
                      }}
                    />

                    {focusViewLOEnabled ? (
                      <NavItemHeader
                        activeItemValues={['jobs_easy_create']}
                        icon={IconWeOffer}
                        // @TODO: replace with filled icon
                        activeIcon={IconWeOffer}
                        title={formatMessage({
                          id: 'MAINNAV_JOBS_EASY_CREATE',
                        })}
                        href="https://www.xing.com/recruiting/jobs/create"
                        tracking={{
                          nwt_nav: 'job_ad',
                          sc_o: 'navigation_job_ad',
                          sc_cmp: 'oowcbc0e3c5',
                        }}
                      />
                    ) : (
                      <>
                        <NavItemHeader
                          activeItemValues={['companies']}
                          icon={IconCompanies}
                          activeIcon={IconCompaniesFilled}
                          title={formatMessage({ id: 'MAINNAV_COMPANIES' })}
                          href="https://www.xing.com/companies"
                          tracking={{
                            nwt_nav: 'companies',
                            sc_o: 'navigation_companies',
                          }}
                        />
                        <NavItemHeader
                          icon={IconContacts}
                          activeIcon={IconContactsFilled}
                          title={formatMessage({ id: 'MAINNAV_NETWORK' })}
                          href="https://www.xing.com/network"
                          activeItemValues={['members']}
                          tracking={{
                            nwt_nav: 'network',
                            sc_o: 'navigation_network',
                          }}
                        />
                        <NavItemHeader
                          icon={IconNews}
                          activeIcon={IconNewsFilled}
                          title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
                          href="https://www.xing.com/insights"
                          activeItemValues={['insights']}
                          tracking={{
                            nwt_nav: 'insights',
                            sc_o: 'navigation_insights',
                          }}
                        />
                      </>
                    )}
                  </StyledOnlyOnBigScreenContainer>
                  <ActivityCenterButton
                    title={formatMessage({ id: 'MAINNAV_ACTIVITY_CENTER' })}
                    href="https://www.xing.com/activity?sc_o=navigation_activity"
                  />
                  <ActionButtonSpace>
                    <LoginButton loginLink={loginLink} />
                  </ActionButtonSpace>
                  <StyledOnlyOnBigScreenContainer>
                    <ProductsMenuWithItemsLoggedOut />
                  </StyledOnlyOnBigScreenContainer>
                </>
              ) : (
                <HeaderNavItemsLoggedIn />
              )}
            </StyledOuterActionsContainer>
          </StyledHeader>
        </Section>
      </StyledHeaderWrapper>
    </StyledHeaderPlacebo>
  );
};
